import { Image, ImageBinary } from "db/definitions";
import batchCreateImages from "utils/batchCreateImages";
import { recordSentry } from "utils/recordSentry";
import { handleBatchCrud } from "utils/handleBatchCrud";
import { handleToolboxResponse } from "utils/s3/handleToolboxResponse";
import createImageRoute from "utils/toolboxQueries/createImageRoute";
import deleteImageRoute from "utils/toolboxQueries/deleteImageRoute";
import { getCorrectS3Url } from "../../utils/s3/getCorrectS3Url";
import { db } from "../db";

/**
 * Given an array of imageBinary ids, deletes all of them from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids An array of ids
 */
export async function bulkDeleteImageBinaryDocs(ids: string[]): Promise<void> {
	await db.imageBinary.bulkDelete(ids);
}

/**
 * Given all of the keys, retrieves all imageBinaries with those keys
 * @author Eric Webb <ewebb@factorearth.com>
 * @param keys The keys of the imageBinary docs we want to grab
 * @returns The imageBinary documents that exist
 */
export async function bulkGetImageBinaries(keys: string[]): Promise<ImageBinary[]> {
	const binaries = await db.imageBinary.bulkGet(keys);
	const filtered = binaries.filter((binary): binary is ImageBinary => binary !== undefined);
	return filtered;
}

/**
 * Given an imageBinary object, creates the document in the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param document The imageBinary document object that we want to store
 */
export async function createImageBinary(document: ImageBinary): Promise<void> {
	await db.imageBinary.put(document);
}

/**
 * Given the image document key and file, creates an imageBinary object and saves it to the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param key The key for the image document
 * @param file The  file we want to save on the document
 * @returns The imageBinary document, or undefined if it was unable to create
 */
export async function createImageBinaryAndSave(key: string, file: File): Promise<ImageBinary> {
	const doc = createImageBinaryDocObject(key, file);
	await createImageBinary(doc);
	return doc;
}

/**
 * Given a key and a file, created an object with the properties and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param key The id of the imageBinary doc
 * @param file The file we're saving on the document
 * @returns A document object ready to be put in the db
 */
function createImageBinaryDocObject(key: string, file: File): ImageBinary {
	const doc = {
		key,
		binary: file,
	};
	return doc;
}

/**
 * Given the key of an imageBinary object, deletes it from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param key The id of the imageBinary document
 */
export async function deleteImageBinary(key: string): Promise<void> {
	await db.imageBinary.delete(key);
}

/**
 * Given the keys for the images we want to delete, loops through them and deletes them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param keys The keys for the s3 objects (images) we want to delete
 */
export async function deleteImagesFromS3(keys: string[]) {
	if (keys.length > 0) {
		const promises = [];
		for (const key of keys) {
			promises.push(deleteImageLambda(key));
		}
		await Promise.all(promises);
	}

	async function deleteImageLambda(key: string) {
		const response = await deleteImageRoute(key);
		handleToolboxResponse(response);
	}
}

/**
 * Given an array of keys, downloads each of them to image binary docs locally
 * @author Eric Webb <ewebb@factorearth.com>
 * @param keys The keys of the images we need to save
 */
export async function getAllImagesFromOnlineAndSaveToBinary(keys: string[]) {
	const promises = [];
	for (const key of keys) {
		promises.push(createImageLambda(key));
	}
	await Promise.all(promises);
	async function createImageLambda(key: string) {
		const imageFile = await getImageBinaryOnline(key);
		if (imageFile) {
			await createImageBinaryAndSave(key, imageFile);
		} else {
			recordSentry({
				error: new Error(`Failed to download image from S3 with key ${key}. getImageBinaryOnline returned undefined`),
				msg: "Failed to download image from S3",
			});
		}
	}
}

/**
 * Given an array of keys, gets their offline image binary and saves them to the s3 bucket
 * @author Eric Webb <ewebb@factorearth.com>
 * @param keys The keys of the images we need to save
 * @param images The imageDocs we need to save. Keys will likely be modified because of the compression process, so they need to be here
 */
export async function getAllImagesFromOfflineAndSaveToS3(keys: string[], images: Image[]) {
	// Collect binary key renames to apply only after the image docs are persisted to DynamoDB.
	// Keeping this separate prevents a race where a failed upload for one image causes
	// batchCreateImages to be skipped, leaving successfully-compressed images with a stale .jpg
	// key in DynamoDB while S3 already has the .webp version.
	const pendingBinaryKeyUpdates: { oldKey: string; newKey: string }[] = [];

	if (keys.length > 0) {
		// Use allSettled so a single upload failure does not abort the rest of the batch.
		await Promise.allSettled(keys.map((key) => getImageBinarySaveToS3AndUpdateKey(key)));
	}
	// These need to be awaited first since they're modifying the image docs
	await handleBatchCrud(images, batchCreateImages, 25);
	// Update binary keys only after the image docs are safely in DynamoDB.
	await Promise.allSettled(pendingBinaryKeyUpdates.map(({ oldKey, newKey }) => updateImageBinaryDocKey(oldKey, newKey)));

	async function getImageBinarySaveToS3AndUpdateKey(key: string) {
		const imageBinary = await getImageBinaryById(key);
		if (imageBinary) {
			const response = await createImageRoute(imageBinary.binary);
			const newKey = handleToolboxResponse(response);
			// HEY YOU! This is finding the image and changing the key to whatever the compresed key is. This is needed for both online photos, and offline. Offline images will get
			// this change because the saveStuff function will after saving to remote, save to local. Both functions get the same image instances, so changing it here fixes it there
			// - Eric
			const updatedImage = images.find((img) => img.key === key);
			if (updatedImage && newKey) {
				updatedImage.key = newKey;
				// Because of the note above, the imageDoc will have the new key, but we need the binary to also have that correct key rather than the old one.
				// Defer this update so it only runs after batchCreateImages succeeds.
				pendingBinaryKeyUpdates.push({ oldKey: key, newKey });
			}
		}
	}
}

/**
 * Given the key for the image binary, queries and retrieves the document
 * @author Eric Webb <ewebb@factorearth.com>
 * @param key The id (s3 key) if the imageBinary document
 * @returns The imageBinary document
 */
export async function getImageBinaryById(key: string): Promise<ImageBinary | undefined> {
	const imageBinary = await db.imageBinary.get(key);
	return imageBinary;
}

/**
 * Given the s3 key, gets the url for the image, downloads it as a blob, converts it to a file, and returns it
 * @author Eric Webb <ewebb@factorearth.com>
 * @param key The s3 key for the image
 * @returns The downloaded file, or undefined if the response failed
 */
export async function getImageBinaryOnline(key: string): Promise<File | void> {
	const retrievedUrl = await getCorrectS3Url(key);
	if (key) {
		const response = await fetch(retrievedUrl);
		if (response.ok) {
			const blob = await response.blob();
			const file = new File([blob], key, { type: blob.type });
			return file;
		} else {
			recordSentry({
				error: new Error(`Failed to fetch image from S3 with key ${key}. Response status: ${response.status}`),
				msg: "Failed to fetch image from S3",
			});
		}
	}
}

/**
 * Given the key of an image binary, finds it and changes it to the new provided key
 * @author Eric Webb <ewebb@factorearth.com>
 * @param key The key of the imageBinary doc
 * @param newKey The new key for the imageBinary doc
 */
async function updateImageBinaryDocKey(key: string, newKey: string) {
	await db.imageBinary.update(key, { key: newKey });
}
